import { Box } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { ReactElement, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { NavigationTabOption, StyleObj } from '../../@types';
import usePermissions from '../../hooks/usePermissions';
import useFeatureFlaggedTabs from '../../hooks/useFeatureFlaggedTabs';

type TabNavigationProps = {
  tabs: NavigationTabOption[];
  icon?: ReactElement;
  basePath?: string;
};

const styles: StyleObj = {
  container: {
    width: '100%',
    minWidth: 0,
    backgroundColor: 'background.light',
    borderBottom: 1,
    borderColor: 'divider',
  },
  tabs: {
    minHeight: { xs: 40, md: 50 },
    '& .MuiTabs-list': {
      display: 'flex',
      width: '100%',
    },
  },
  defaultTabIndicator: {
    display: 'none',
  },
};

const makeTabStyle = (active: boolean, numberOfTabs: number) => {
  return {
    tab: {
      minWidth: 0,
      textTransform: 'none',
      minHeight: { xs: 40, md: 50 },
      fontWeight: 600,
      fontSize: { xs: '0.75rem', md: '0.875rem' },
      width: `calc(100% / ${numberOfTabs})`,
      borderBottom: active ? '2px solid' : '2px solid transparent',
      transition: 'all 0.2s ease-in-out',
      gap: { xs: 0.5, md: 1 },
      '&.Mui-selected': {
        borderBottomColor: 'primary.main',
      },
      '& .MuiTab-icon': {
        mr: { xs: 0.25 },
        width: { xs: 10, md: 12 },
        height: { xs: 10, md: 12 },
      },
    },
  };
};

const TabNavigation = ({ tabs, icon, basePath }: TabNavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { filterAccessibleTabs } = usePermissions();
  const { filterFeatureFlaggedTabs } = useFeatureFlaggedTabs();

  const filteredAccessibleTabs = filterFeatureFlaggedTabs(filterAccessibleTabs(tabs));
  const validTabValues = filteredAccessibleTabs.map((tab) => tab.value);

  const pathSegment = basePath
    ? location.pathname.substring(location.pathname.indexOf(basePath) + basePath.length + 1).split('/')[0]
    : location.pathname.split('/').pop();

  const [currentTab, setCurrentTab] = useState<string | false>(false);

  useEffect(() => {
    if (pathSegment && validTabValues.includes(pathSegment)) {
      setCurrentTab(pathSegment);
    } else {
      setCurrentTab(false);
    }
  }, [validTabValues, pathSegment]);

  const handleTabChange = (_: React.SyntheticEvent, selectedTabValue: string) => {
    setCurrentTab(selectedTabValue);
    navigate(`./${selectedTabValue}`);
  };

  if (!currentTab) return <Outlet />;

  return (
    <>
      <Box sx={styles.container}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          textColor='inherit'
          sx={styles.tabs}
          slotProps={{
            indicator: {
              sx: styles.defaultTabIndicator,
            },
          }}
        >
          {filteredAccessibleTabs.map(({ label, value }) => (
            <Tab
              icon={icon}
              iconPosition='start'
              key={value}
              label={label}
              value={value}
              sx={makeTabStyle(currentTab === value, filteredAccessibleTabs.length).tab}
            />
          ))}
        </Tabs>
      </Box>
      <Outlet />
    </>
  );
};

export default TabNavigation;
